import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Inbox, Mail, Archive, Folder, Eye, EyeOff, ChevronLeft, ChevronRight, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, toastErr } from "@/lib/api";

const ICON = { all: Inbox, unread: Mail, archived: Archive };

function Chip({ f, on, onSelect, admin, toggleVis, onDropConv, id }) {
  const [over, setOver] = useState(false);
  const [menu, setMenu] = useState(false);
  const Icon = ICON[f.id] || Folder;
  const dnd = !f.builtin && onDropConv ? {
    onDragOver: (e) => { if (e.dataTransfer.types.includes("text/dopamine-conv")) { e.preventDefault(); setOver(true); } },
    onDragLeave: () => setOver(false),
    onDrop: (e) => { e.preventDefault(); setOver(false); const cid = e.dataTransfer.getData("text/dopamine-conv"); if (cid) onDropConv(cid, f.id); },
  } : {};
  return (
    <div {...dnd} id={id} onContextMenu={(e) => { if (admin) { e.preventDefault(); setMenu(true); } }} data-testid={`folder-drop-${f.id}`}
      className={`shrink-0 flex items-center rounded-full border text-xs font-semibold transition-colors ${over ? "ring-2 ring-emerald-400" : ""} ${on ? "border-violet-500 bg-violet-600 text-white" : "border-[#232738] text-slate-400 hover:text-white hover:border-violet-500/50"}`}>
      <button onClick={() => onSelect(f.id)} aria-pressed={on} className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 whitespace-nowrap" data-testid={`inbox-folder-${f.id}`}>
        {f.builtin || !f.emoticon || f.title.includes(f.emoticon) ? <Icon className="h-3.5 w-3.5 shrink-0" /> : <span>{f.emoticon}</span>}<span>{f.title}</span>
        {f.unread > 0 && (f.paid && !f.builtin
          ? <span title={`${f.unread} paganti non letti`} className="num rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-black" data-testid={`folder-paid-${f.id}`}>{f.unread}</span>
          : <span className="num rounded-full bg-red-600 px-1.5 text-[10px] text-white" data-testid={`folder-unread-${f.id}`}>{f.unread}</span>)}
      </button>
      {admin && !f.locked && (
        <DropdownMenu open={menu} onOpenChange={setMenu}>
          <DropdownMenuTrigger className="pr-2 opacity-60 hover:opacity-100" data-testid={`folder-menu-${f.id}`}><MoreVertical className="h-3.5 w-3.5" /></DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#12141D] border-[#232738]">
            <DropdownMenuItem onClick={() => toggleVis(f)} data-testid={`folder-visibility-${f.id}`}><EyeOff className="h-4 w-4 mr-2" />Nascondi nel gestionale</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// ONE horizontal row: Tutte, Telegram folders (Telegram order), Archiviate. Scrolls with wheel/trackpad and < > arrows.
function WrapBar({ refEl, testid, children }) {
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  useEffect(() => { const el = refEl.current; if (el) setMore(el.scrollHeight > el.clientHeight + 2 || open); }, [children, open, refEl]);
  return (
    <div className="shrink-0 border-b border-[#232738] px-2.5 py-2">
      <div ref={refEl} className={`flex flex-wrap items-center gap-1.5 overflow-hidden ${open ? "max-h-[40vh] overflow-y-auto thin-scroll" : "max-h-[100px]"}`} data-testid={testid}>{children}</div>
      {more && <button onClick={() => setOpen((o) => !o)} className="mt-1 text-[11px] font-semibold text-violet-300 hover:text-violet-200" data-testid={`${testid}-more`}>{open ? "− meno ▴" : "+ altre ▾"}</button>}
    </div>
  );
}

export function FolderBar({ accountId, folders, folder, onSelect, admin, onChanged, onDropConv, testid = "folder-bar", extra = null, wrap = false }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ l: false, r: false });
  const upd = () => { const el = ref.current; if (el) setEdge({ l: el.scrollLeft > 2, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 }); };
  useEffect(() => {
    upd();
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    const wheel = (e) => { if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) { e.preventDefault(); el.scrollLeft += e.deltaY; } };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => { ro.disconnect(); el.removeEventListener("wheel", wheel); };
  }, [folders]);
  useEffect(() => { document.getElementById(`${testid}-${folder}`)?.scrollIntoView({ block: "nearest", inline: "nearest" }); }, [folder, folders, testid]);
  const toggleVis = async (f) => {
    try { await api.patch(`/telegram/accounts/${accountId}/folders/${f.id}`, { visible: !f.visible }); toast.success(f.visible ? `"${f.title}" nascosta nel gestionale` : `"${f.title}" visibile`); onChanged(); } catch (e) { toastErr(e); }
  };
  const shown = folders.filter((f) => f.visible);
  const hidden = folders.filter((f) => !f.visible);
  const arrow = (dir) => ref.current?.scrollBy({ left: dir * 260, behavior: "smooth" });
  const chips = shown.map((f) => <Chip key={f.id} id={`${testid}-${f.id}`} f={f} on={folder === f.id} onSelect={onSelect} admin={admin} toggleVis={toggleVis} onDropConv={onDropConv} />);
  const hiddenMenu = admin && hidden.length > 0 && (
    <DropdownMenu>
      <DropdownMenuTrigger className="shrink-0 rounded-full border border-dashed border-[#334155] px-3 py-1.5 text-xs text-slate-500 hover:text-white" data-testid="folder-hidden-menu">Nascoste ({hidden.length})</DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#12141D] border-[#232738]">
        {hidden.map((f) => <DropdownMenuItem key={f.id} onClick={() => toggleVis(f)} data-testid={`folder-show-${f.id}`}><Eye className="h-4 w-4 mr-2" />Mostra "{f.title}"</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  // Messages left column: folder labels wrap on max 3 lines, then '+ altre ▾'; the chats always start right below
  if (wrap) return <WrapBar refEl={ref} testid={testid}>{chips}{hiddenMenu}{extra}</WrapBar>;
  return (
    <div className="shrink-0 flex items-center gap-1 border-b border-[#232738] bg-[#0D0E14] px-1.5 py-1.5" data-testid={testid}>
      <button onClick={() => arrow(-1)} disabled={!edge.l} className="shrink-0 p-1 rounded-full text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-20" data-testid={`${testid}-left`}><ChevronLeft className="h-4 w-4" /></button>
      <div ref={ref} onScroll={upd} className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth" data-testid={`${testid}-row`}>
        {chips}
        {hiddenMenu}
      </div>
      <button onClick={() => arrow(1)} disabled={!edge.r} className="shrink-0 p-1 rounded-full text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-20" data-testid={`${testid}-right`}><ChevronRight className="h-4 w-4" /></button>
      {extra}
    </div>
  );
}
